import { format } from "date-fns";
import { Inventory, Invoice, MonthlyReport, Payment, PriceTier, Product, ProductReport, PurchaseOrder, ReportType, SalesOrder } from "@/core";
import * as XLSX from "xlsx";
import { getPurchaseOrderTotal, getSalesOrderTotal } from "./order-total";
import {
  formatShortId,
  getPurchaseOrderDisplayStatus,
  resolveProductCode,
} from "@/components/operations/operations-shared";

export interface SortingState {
  id: string;
  desc: boolean;
}
export function applySortingToReports<T extends ProductReport | MonthlyReport>(
  reports: T[],
  sorting: SortingState[],
): T[] {
  if (!sorting || sorting.length === 0) {
    return reports;
  }

  const sortedReports = [...reports];
  const sortConfig = sorting[0];

  sortedReports.sort((a, b) => {
    let aValue: unknown;
    let bValue: unknown;

    if (sortConfig.id === "period") {
      if ("year" in a && "month" in a && "year" in b && "month" in b) {
        aValue = a.year * 100 + a.month;
        bValue = b.year * 100 + b.month;
      }
    } else if (sortConfig.id === "totalDeals") {
      if ("totalDeals" in a && "totalDeals" in b) {
        aValue = a.totalDeals || 0;
        bValue = b.totalDeals || 0;
      } else {
        const aWon = "wonDeals" in a ? a.wonDeals : 0;
        const aLost = "lostDeals" in a ? a.lostDeals : 0;
        const aPending = "numberOfPendingDeals" in a ? a.numberOfPendingDeals : 0;
        const bWon = "wonDeals" in b ? b.wonDeals : 0;
        const bLost = "lostDeals" in b ? b.lostDeals : 0;
        const bPending = "numberOfPendingDeals" in b ? b.numberOfPendingDeals : 0;
        aValue = aWon + aLost + aPending;
        bValue = bWon + bLost + bPending;
      }
    } else {
      aValue = (a as Record<string, unknown>)[sortConfig.id];
      bValue = (b as Record<string, unknown>)[sortConfig.id];
    }

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    let comparison = 0;
    if (typeof aValue === "string" && typeof bValue === "string") {
      comparison = aValue.localeCompare(bValue);
    } else if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    } else if (aValue instanceof Date && bValue instanceof Date) {
      comparison = aValue.getTime() - bValue.getTime();
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    return sortConfig.desc ? -comparison : comparison;
  });

  return sortedReports;
}

function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  let stringValue = String(value);

  if (stringValue.includes('"')) {
    stringValue = stringValue.replace(/"/g, '""');
  }

  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    return `"${stringValue}"`;
  }

  return stringValue;
}

function formatDateForCsv(date: Date | string | null | undefined): string {
  if (!date) return "";

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return "";

  return format(dateObj, "yyyy-MM-dd HH:mm:ss");
}

function formatNumberForCsv(num: number | undefined | null): string {
  if (num === null || num === undefined || isNaN(num) || !isFinite(num)) {
    return "0";
  }
  return num.toString();
}

function formatCurrencyForCsv(amount: number | undefined | null): string {
  if (amount === null || amount === undefined || isNaN(amount) || !isFinite(amount)) {
    return "0.00";
  }
  return amount.toFixed(2);
}

function formatPercentageForCsv(num: number | undefined | null): string {
  if (num === null || num === undefined || isNaN(num) || !isFinite(num)) {
    return "0.00";
  }
  return num.toFixed(2);
}

export function exportProductReportsToCsv(
  reports: ProductReport[],
  transactionType?: "buy" | "sell" | "both",
  filename?: string,
): void {
  if (!reports || reports.length === 0) {
    console.warn("No data to export");
    return;
  }

  const headers = [
    "Product Name",
    "Data As Of",
    "Last Updated",
    "Total Deals",
    "Number of Pending Deals",
    "Won Deals",
    "Lost Deals",
    "Win Rate (%)",
    "Rejection Rate (%)",
    "Total Quantity",
    "Total Revenue ($)",
    "Lost Revenue ($)",
    "Potential Revenue ($)",
    "Average Deal Value ($)",
    "Pending % of Deals",
    "Won % of Total",
    "Lost % of Total",
    "Bought Miles",
    "Sold Miles",
  ];

  const rows = reports.map((report) => [
    escapeCsvValue(report.productName),
    formatDateForCsv(report.dataAsOf),
    formatDateForCsv(report.lastUpdated),
    formatNumberForCsv(report.totalDeals),
    formatNumberForCsv(report.numberOfPendingDeals),
    formatNumberForCsv(report.wonDeals),
    formatNumberForCsv(report.lostDeals),
    formatPercentageForCsv(report.winRate),
    formatPercentageForCsv(report.rejectionRate),
    formatNumberForCsv(report.totalQuantity),
    formatCurrencyForCsv(report.totalRevenue),
    formatCurrencyForCsv(report.lostRevenue),
    formatCurrencyForCsv(report.potentialRevenue),
    formatCurrencyForCsv(report.averageDealValue),
    formatPercentageForCsv(report.pendingPercentageOfDeals),
    formatPercentageForCsv(report.wonPercentageOfTotal),
    formatPercentageForCsv(report.lostPercentageOfTotal),
    formatNumberForCsv(report.boughtMiles),
    formatNumberForCsv(report.soldMiles),
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.join(","))
    .join("\n");

  // Generate filename with transaction type
  const transactionTypeLabel = transactionType === "buy" ? "purchases" : 
                              transactionType === "sell" ? "sales" : 
                              "all-transactions";
  const defaultFilename = `${transactionTypeLabel}-product-reports-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename || defaultFilename);
}

export function exportMonthlyReportsToCsv(
  reports: MonthlyReport[],
  transactionType?: "buy" | "sell" | "both",
  filename?: string,
): void {
  if (!reports || reports.length === 0) {
    console.warn("No data to export");
    return;
  }

  const headers = [
    "Month Key",
    "Year",
    "Month",
    "Data As Of",
    "Last Updated",
    "Total Deals",
    "Number of Pending Deals",
    "Won Deals",
    "Lost Deals",
    "Win Rate (%)",
    "Rejection Rate (%)",
    "Total Quantity",
    "Total Revenue ($)",
    "Lost Revenue ($)",
    "Potential Revenue ($)",
    "Average Deal Value ($)",
    "Pending % of Deals",
    "Won % of Deals",
    "Lost % of Deals",
    "Unique Products",
    "Unique Contacts",
    "Top Product ID",
    "Top Product Name",
    "Top Product Revenue ($)",
    "Bought Miles",
    "Sold Miles",
  ];

  const rows = reports.map((report) => [
    escapeCsvValue(report.monthKey),
    formatNumberForCsv(report.year),
    formatNumberForCsv(report.month),
    formatDateForCsv(report.dataAsOf),
    formatDateForCsv(report.lastUpdated),
    formatNumberForCsv(report.totalDeals),
    formatNumberForCsv(report.numberOfPendingDeals),
    formatNumberForCsv(report.wonDeals),
    formatNumberForCsv(report.lostDeals),
    formatPercentageForCsv(report.winRate),
    formatPercentageForCsv(report.rejectionRate),
    formatNumberForCsv(report.totalQuantity),
    formatCurrencyForCsv(report.totalRevenue),
    formatCurrencyForCsv(report.lostRevenue),
    formatCurrencyForCsv(report.potentialRevenue),
    formatCurrencyForCsv(report.averageDealValue),
    formatPercentageForCsv(report.pendingPercentageOfDeals),
    formatPercentageForCsv(report.wonPercentageOfTotal),
    formatPercentageForCsv(report.lostPercentageOfTotal),
    formatNumberForCsv(report.uniqueProducts),
    formatNumberForCsv(report.uniqueCustomers),
    escapeCsvValue(report.topProductId),
    escapeCsvValue(report.topProductName),
    formatCurrencyForCsv(report.topProductRevenue),
    formatNumberForCsv(report.boughtMiles),
    formatNumberForCsv(report.soldMiles),
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.join(","))
    .join("\n");

  // Generate filename with transaction type
  const transactionTypeLabel = transactionType === "buy" ? "purchases" : 
                              transactionType === "sell" ? "sales" : 
                              "all-transactions";
  const defaultFilename = `${transactionTypeLabel}-monthly-reports-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename || defaultFilename);
}

export function exportReportsToCsv(
  reports: (ProductReport | MonthlyReport)[],
  reportType: ReportType,
  transactionType?: "buy" | "sell" | "both",
  filename?: string,
): void {
  if (!reports || reports.length === 0) {
    console.warn("No data to export");
    return;
  }

  if (reportType === ReportType.PRODUCT) {
    exportProductReportsToCsv(reports as ProductReport[], transactionType, filename);
  } else if (reportType === ReportType.MONTHLY) {
    exportMonthlyReportsToCsv(reports as MonthlyReport[], transactionType, filename);
  } else {
    console.error("Unknown report type:", reportType);
  }
}

function downloadCsv(csvContent: string, filename: string): void {
  // Add UTF-8 BOM for Excel compatibility
  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  // Create download link and trigger click
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadExcel(rows: unknown[][], filename: string, sheetName: string): void {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// ─── Sales Orders ────────────────────────────────────────────────────────────

const SALES_ORDER_HEADERS = [
  "ID",
  "Inventory ID",
  "Purchase Order IDs",
  "Purchase Order Vendors",
  "Client",
  "Product",
  "Product Code",
  "Status",
  "Sales Channel",
  "Miles Sold",
  "Revenue ($)",
  "CPM (¢/mi)",
  "Tax Cost ($)",
  "Total ($)",
  "Flight Date",
  "Booking End Date",
  "Created At",
];

function getSalesOrderCpmCents(so: SalesOrder): number | null {
  if (!Number.isFinite(so.totalMilesSold) || so.totalMilesSold <= 0) {
    return null;
  }
  return (so.revenue / so.totalMilesSold) * 100;
}

/**
 * Purchase orders reachable from a sell order, via the shared inventory — the
 * only link between the two. All of them are listed, since nothing records
 * which PO's miles a given sell order consumed.
 */
export type PurchaseOrdersByInventoryId = Map<
  string,
  Array<Pick<PurchaseOrder, "id" | "vendorDisplayName" | "vendorEmail">>
>;

function salesOrderToRow(
  so: SalesOrder,
  productCodeById?: Map<string, string>,
  purchaseOrdersByInventoryId?: PurchaseOrdersByInventoryId,
): unknown[] {
  const cpm = getSalesOrderCpmCents(so);
  const linkedPurchaseOrders =
    purchaseOrdersByInventoryId?.get(so.inventoryId) ?? [];
  return [
    so.id,
    formatShortId(so.inventoryId),
    linkedPurchaseOrders.map((po) => po.id).join("; "),
    linkedPurchaseOrders
      .map((po) => po.vendorDisplayName || po.vendorEmail || "Unknown vendor")
      .join("; "),
    so.clientDisplayName || so.clientEmail || "",
    so.productName,
    resolveProductCode({
      productCode: productCodeById?.get(so.productId),
      productName: so.productName,
    }),
    so.status,
    so.salesChannel,
    formatNumberForCsv(so.totalMilesSold),
    formatCurrencyForCsv(so.revenue),
    cpm === null ? "" : cpm.toFixed(2),
    formatCurrencyForCsv(so.taxCost),
    formatCurrencyForCsv(getSalesOrderTotal({ revenue: so.revenue, taxCost: so.taxCost, totalAmount: so.totalAmount })),
    formatDateForCsv(so.flightDate),
    formatDateForCsv(so.bookingEndDate),
    formatDateForCsv(so.createdAt),
  ];
}

export function exportSalesOrdersToCsv(
  orders: SalesOrder[],
  productCodeById?: Map<string, string>,
  filename?: string,
  purchaseOrdersByInventoryId?: PurchaseOrdersByInventoryId,
): void {
  if (!orders.length) return;
  const csvContent = [
    SALES_ORDER_HEADERS,
    ...orders.map((so) =>
      salesOrderToRow(so, productCodeById, purchaseOrdersByInventoryId),
    ),
  ]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `sales-orders-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}

export function exportSalesOrdersToExcel(
  orders: SalesOrder[],
  productCodeById?: Map<string, string>,
  filename?: string,
  purchaseOrdersByInventoryId?: PurchaseOrdersByInventoryId,
): void {
  if (!orders.length) return;
  const defaultFilename = `sales-orders-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
  downloadExcel(
    [
      SALES_ORDER_HEADERS,
      ...orders.map((so) =>
        salesOrderToRow(so, productCodeById, purchaseOrdersByInventoryId),
      ),
    ],
    filename ?? defaultFilename,
    "Sales Orders",
  );
}

// ─── Purchase Orders ─────────────────────────────────────────────────────────

const PURCHASE_ORDER_HEADERS = [
  "ID",
  "Inventory ID",
  "Vendor",
  "Product",
  "Status",
  "Payment Status",
  "Source",
  "Payment Timing",
  "Payment Method",
  "Miles Bought",
  "Buy Price ($)",
  "Tax Cost ($)",
  "Total ($)",
  "Next Payment Date",
  "Created At",
];

function purchaseOrderToRow(po: PurchaseOrder): unknown[] {
  return [
    po.id,
    formatShortId(po.inventoryId),
    po.vendorDisplayName || po.vendorEmail || "",
    po.productName,
    // Cancelled POs are stored as "voided"; export what the UI shows.
    getPurchaseOrderDisplayStatus(po),
    po.paymentStatus,
    po.source,
    po.paymentTiming,
    po.paymentMethod || "",
    formatNumberForCsv(po.amountBought),
    formatCurrencyForCsv(po.buyPrice),
    formatCurrencyForCsv(po.taxCost),
    formatCurrencyForCsv(getPurchaseOrderTotal({ buyPrice: po.buyPrice, taxCost: po.taxCost, totalAmount: po.totalAmount })),
    formatDateForCsv(po.nextPaymentDate),
    formatDateForCsv(po.createdAt),
  ];
}

export function exportPurchaseOrdersToCsv(orders: PurchaseOrder[], filename?: string): void {
  if (!orders.length) return;
  const csvContent = [PURCHASE_ORDER_HEADERS, ...orders.map(purchaseOrderToRow)]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `purchase-orders-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}

export function exportPurchaseOrdersToExcel(orders: PurchaseOrder[], filename?: string): void {
  if (!orders.length) return;
  const defaultFilename = `purchase-orders-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
  downloadExcel([PURCHASE_ORDER_HEADERS, ...orders.map(purchaseOrderToRow)], filename ?? defaultFilename, "Purchase Orders");
}

// ─── Inventory ────────────────────────────────────────────────────────────────

const INVENTORY_HEADERS = [
  "ID",
  "Name",
  "Product",
  "Status",
  "Account Status",
  "Account Owner",
  "Username",
  "Account Tier",
  "Tier %",
  "Purchased Miles",
  "Sold Miles",
  "Available Miles",
  "PO Count",
  "SO Count",
  "Expiration Info",
  "Updated At",
  "Created At",
];

function inventoryToRow(inv: Inventory): unknown[] {
  return [
    inv.id,
    inv.name,
    inv.productName,
    inv.status,
    inv.accountStatus,
    inv.accountOwnerName || "",
    inv.username || "",
    inv.statusTier?.name || "",
    formatNumberForCsv(inv.statusTier?.percentage),
    formatNumberForCsv(inv.totalPurchasedMiles),
    formatNumberForCsv(inv.totalSoldMiles),
    formatNumberForCsv(inv.availableMiles),
    formatNumberForCsv(inv.purchaseOrderCount),
    formatNumberForCsv(inv.sellOrderCount),
    inv.expirationInfo || "",
    formatDateForCsv(inv.updatedAt),
    formatDateForCsv(inv.createdAt),
  ];
}

export function exportInventoryToCsv(inventories: Inventory[], filename?: string): void {
  if (!inventories.length) return;
  const csvContent = [INVENTORY_HEADERS, ...inventories.map(inventoryToRow)]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `inventory-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}

export function exportInventoryToExcel(inventories: Inventory[], filename?: string): void {
  if (!inventories.length) return;
  const defaultFilename = `inventory-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
  downloadExcel([INVENTORY_HEADERS, ...inventories.map(inventoryToRow)], filename ?? defaultFilename, "Inventory");
}

// ─── Products ─────────────────────────────────────────────────────────────────

const PRODUCT_HEADERS = [
  "ID",
  "Name",
  "Product Code",
  "Status",
  "Category",
  "Type",
  "Payment Option",
  "Min Buy",
  "Max Buy",
  "Min Sell",
  "Max Sell",
  "Buy Price Tiers",
  "Sell Price Tiers",
  "Status Tiers",
  "Updated At",
  "Created At",
];

function formatPriceTiersForCsv(tiers: PriceTier[] | undefined): string {
  return (tiers ?? [])
    .map((tier) => `${tier.minRange}-${tier.maxRange}: $${tier.price}`)
    .join("; ");
}

function productToRow(product: Product): unknown[] {
  return [
    product.id,
    product.name,
    resolveProductCode({
      productCode: product.productCode,
      productName: product.name,
    }),
    product.inactive ? "inactive" : "active",
    product.category,
    product.buyOrSell,
    product.paymentOption || "",
    formatNumberForCsv(product.minimumValueForBuy),
    formatNumberForCsv(product.maximumValueForBuy),
    formatNumberForCsv(product.minimumValueForSell),
    formatNumberForCsv(product.maximumValueForSell),
    formatPriceTiersForCsv(product.buyPrices),
    formatPriceTiersForCsv(product.sellPrices),
    (product.statusTiers ?? [])
      .map((tier) => `${tier.name} (${tier.percentage}%)`)
      .join("; "),
    formatDateForCsv(product.updatedAt),
    formatDateForCsv(product.createdAt),
  ];
}

export function exportProductsToCsv(products: Product[], filename?: string): void {
  if (!products.length) return;
  const csvContent = [PRODUCT_HEADERS, ...products.map(productToRow)]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `products-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}

// ─── Payments ─────────────────────────────────────────────────────────────────

const PAYMENT_HEADERS = [
  "ID",
  "Applies To",
  "Applies To ID",
  "Amount ($)",
  "Method",
  "Status",
  "Paid At",
  "Created At",
];

function paymentToRow(payment: Payment): unknown[] {
  return [
    payment.id,
    payment.appliesToType,
    payment.appliesToId,
    formatCurrencyForCsv(payment.amount),
    payment.method,
    payment.status,
    formatDateForCsv(payment.paidAt),
    formatDateForCsv(payment.createdAt),
  ];
}

export function exportPaymentsToCsv(payments: Payment[], filename?: string): void {
  if (!payments.length) return;
  const csvContent = [PAYMENT_HEADERS, ...payments.map(paymentToRow)]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `payments-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}

export function exportPaymentsToExcel(payments: Payment[], filename?: string): void {
  if (!payments.length) return;
  const defaultFilename = `payments-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
  downloadExcel([PAYMENT_HEADERS, ...payments.map(paymentToRow)], filename ?? defaultFilename, "Payments");
}

// ─── Invoices ─────────────────────────────────────────────────────────────────

const INVOICE_HEADERS = [
  "ID",
  "Inventory ID",
  "Sales Order ID",
  "Purchase Order ID",
  "Amount Due ($)",
  "Currency",
  "Status",
  "Issued At",
  "Due At",
  "Created At",
];

function invoiceToRow(invoice: Invoice): unknown[] {
  return [
    invoice.id,
    formatShortId(invoice.inventoryId),
    invoice.soId || "",
    invoice.poId || "",
    formatCurrencyForCsv(invoice.amountDue),
    invoice.currency,
    invoice.status,
    formatDateForCsv(invoice.issuedAt),
    formatDateForCsv(invoice.dueAt),
    formatDateForCsv(invoice.createdAt),
  ];
}

export function exportInvoicesToCsv(invoices: Invoice[], filename?: string): void {
  if (!invoices.length) return;
  const csvContent = [INVOICE_HEADERS, ...invoices.map(invoiceToRow)]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `invoices-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}

export function exportInvoicesToExcel(invoices: Invoice[], filename?: string): void {
  if (!invoices.length) return;
  const defaultFilename = `invoices-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
  downloadExcel([INVOICE_HEADERS, ...invoices.map(invoiceToRow)], filename ?? defaultFilename, "Invoices");
}

// ─── Transactions inbox (PayPal / Mercury / Airwallex) ──────────────────────

export interface TransactionExportRow {
  source: string;
  direction: string;
  date?: Date;
  amount: number;
  fee: number;
  currency: string;
  /** The other party — who we paid (outgoing) or who paid us (incoming). */
  counterparty: string;
  /** Our own account the money left from / landed in. */
  account: string;
  /** Free-text bank memo / note — kept apart from the counterparty. */
  description: string;
  status: string;
  order: string;
  transactionId: string;
}

const TRANSACTION_HEADERS = [
  "Source",
  "Direction",
  "Date",
  "Amount ($)",
  "Fee ($)",
  "Currency",
  "Counterparty",
  "Account",
  "Description",
  "Status",
  "Order",
  "Transaction ID",
];

function transactionToRow(t: TransactionExportRow): unknown[] {
  return [
    t.source,
    t.direction,
    formatDateForCsv(t.date),
    formatCurrencyForCsv(t.amount),
    formatCurrencyForCsv(t.fee),
    t.currency,
    t.counterparty,
    t.account,
    t.description,
    t.status,
    t.order,
    t.transactionId,
  ];
}

export function exportTransactionsToCsv(
  rows: TransactionExportRow[],
  filename?: string,
): void {
  if (!rows.length) return;
  const csvContent = [TRANSACTION_HEADERS, ...rows.map(transactionToRow)]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `transactions-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}

// ─── Balances (Mercury / Airwallex) ─────────────────────────────────────────

export interface BalanceExportRow {
  bank: string;
  account: string;
  currency: string;
  available: number;
  current: number;
}

const BALANCE_HEADERS = [
  "Bank",
  "Account",
  "Currency",
  "Available ($)",
  "Current ($)",
];

function balanceToRow(b: BalanceExportRow): unknown[] {
  return [
    b.bank,
    b.account,
    b.currency,
    formatCurrencyForCsv(b.available),
    formatCurrencyForCsv(b.current),
  ];
}

export function exportBalancesToCsv(
  rows: BalanceExportRow[],
  filename?: string,
): void {
  if (!rows.length) return;
  const csvContent = [BALANCE_HEADERS, ...rows.map(balanceToRow)]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
  const defaultFilename = `balances-${format(new Date(), "yyyy-MM-dd")}.csv`;
  downloadCsv(csvContent, filename ?? defaultFilename);
}
